'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, OverlayViewF, OverlayView } from '@react-google-maps/api';
import Image from 'next/image';
import Link from 'next/link';
import type { EnrichedPartner } from './components';
import { getPartnerUrl } from './components';
import { grayscaleMapStyles } from '@/lib/mapStyles';
interface PartnersMapSectionProps {
  partners: EnrichedPartner[];
  title?: string;
  /**
   * Server-resolved Google Maps API key (Sanity settings or env var).
   * Required so this component's loader call matches PropertyMap /
   * ListingsMap exactly — @react-google-maps/api uses a process-level
   * singleton and crashes if successive calls pass different options.
   */
  googleMapsApiKey?: string;
}
const mapContainerStyle = {
  width: '100%',
  height: '100%'
};
const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  styles: grayscaleMapStyles
};
function PartnerMarker(t0) {
  const $ = _c(20);
  const {
    partner,
    isSelected,
    isHovered,
    onClick
  } = t0;
  if (!partner.latitude || !partner.longitude) {
    return null;
  }
  const highlighted = isSelected || isHovered;
  let t1;
  if ($[0] !== partner.latitude || $[1] !== partner.longitude) {
    t1 = {
      lat: partner.latitude,
      lng: partner.longitude
    };
    $[0] = partner.latitude;
    $[1] = partner.longitude;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = `klug-gallery-btn relative -translate-x-1/2 -translate-y-1/2 transition-all duration-300 ${highlighted ? "scale-125 z-20" : "scale-100 z-10 hover:scale-110"}`;
  const t3 = `w-8 h-8 rounded-full overflow-hidden border-2 shadow-md transition-all duration-300 ${highlighted ? "border-[var(--color-gold)] ring-2 ring-[var(--color-gold)]/30" : "border-white hover:border-[var(--color-gold)]"}`;
  let t4;
  if ($[3] !== partner.firstName || $[4] !== partner.lastName || $[5] !== partner.photoUrl) {
    t4 = partner.photoUrl ? <img src={partner.photoUrl} alt={`${partner.firstName} ${partner.lastName}`} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full bg-[#f0f0f0] flex items-center justify-center text-[#aaa]"><svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg></div>;
    $[3] = partner.firstName;
    $[4] = partner.lastName;
    $[5] = partner.photoUrl;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `absolute left-1/2 -translate-x-1/2 -bottom-1 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent transition-colors duration-300 ${isSelected ? "border-t-[var(--color-gold)]" : "border-t-white"}`;
  let t7;
  if ($[10] !== t6) {
    t7 = <div className={t6} />;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onClick || $[13] !== t2 || $[14] !== t5 || $[15] !== t7) {
    t8 = <button onClick={onClick} className={t2}>{t5}{t7}</button>;
    $[12] = onClick;
    $[13] = t2;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t1 || $[18] !== t8) {
    t9 = <OverlayViewF position={t1} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>{t8}</OverlayViewF>;
    $[17] = t1;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function PartnerListCard(t0) {
  const $ = _c(29);
  const {
    partner,
    isSelected,
    onClick,
    onMouseEnter,
    onMouseLeave
  } = t0;
  let t1;
  if ($[0] !== partner) {
    t1 = getPartnerUrl(partner);
    $[0] = partner;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const partnerUrl = t1;
  const cardRef = useRef(null);
  let t2;
  let t3;
  if ($[2] !== isSelected) {
    t2 = () => {
      if (isSelected && cardRef.current) {
        cardRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }
    };
    t3 = [isSelected];
    $[2] = isSelected;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const t4 = `px-3 py-2.5 border-b border-[#e8e6e3] dark:border-gray-800 cursor-pointer transition-all duration-300 ${isSelected ? "bg-[var(--color-gold)]/10 border-l-4 border-l-[var(--color-gold)]" : "hover:bg-[#f8f7f5] dark:hover:bg-[#252525]"}`;
  let t5;
  if ($[5] !== partner.firstName || $[6] !== partner.lastName || $[7] !== partner.photoUrl) {
    t5 = <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden bg-[#f0f0f0] dark:bg-gray-800">{partner.photoUrl ? <Image src={partner.photoUrl} alt={`${partner.firstName} ${partner.lastName}`} width={40} height={40} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full flex items-center justify-center text-[#aaa] dark:text-gray-600"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg></div>}</div>;
    $[5] = partner.firstName;
    $[6] = partner.lastName;
    $[7] = partner.photoUrl;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== partner.firstName || $[10] !== partner.lastName) {
    t6 = <h3 className="font-serif text-sm text-[#1a1a1a] dark:text-white leading-tight">{partner.firstName} {partner.lastName}</h3>;
    $[9] = partner.firstName;
    $[10] = partner.lastName;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== partner.location) {
    t7 = partner.location && <p className="text-[11px] text-[#888] dark:text-gray-500 flex items-center gap-1 mt-0.5 leading-tight"><svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg><span className="truncate">{partner.location}</span></p>;
    $[12] = partner.location;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className="flex-1 min-w-0">{t6}{t7}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== partnerUrl) {
    t9 = <Link href={partnerUrl} onClick={_temp} className="flex-shrink-0 text-[10px] uppercase tracking-wider text-[var(--color-gold)] hover:underline">View</Link>;
    $[17] = partnerUrl;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t8 || $[21] !== t9) {
    t10 = <div className="flex items-center gap-3">{t5}{t8}{t9}</div>;
    $[19] = t5;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== onClick || $[24] !== onMouseEnter || $[25] !== onMouseLeave || $[26] !== t10 || $[27] !== t4) {
    t11 = <div ref={cardRef} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={t4}>{t10}</div>;
    $[23] = onClick;
    $[24] = onMouseEnter;
    $[25] = onMouseLeave;
    $[26] = t10;
    $[27] = t4;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}
function _temp(e) {
  return e.stopPropagation();
}
export default function PartnersMapSection(t0) {
  const $ = _c(44);
  const {
    partners,
    title: t1,
    googleMapsApiKey
  } = t0;
  const title = t1 === undefined ? "Our Partner Network" : t1;
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [hoveredPartner, setHoveredPartner] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const mapRef = useRef(null);
  const apiKey = googleMapsApiKey || process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
  let t2;
  if ($[0] !== apiKey) {
    t2 = {
      googleMapsApiKey: apiKey
    };
    $[0] = apiKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    isLoaded,
    loadError
  } = useJsApiLoader(t2);
  let t3;
  if ($[2] !== partners) {
    t3 = partners.filter(_temp2);
    $[2] = partners;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const partnersWithCoords = t3;
  let t10;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== hoveredPartner || $[5] !== isLoaded || $[6] !== loadError || $[7] !== partners || $[8] !== partnersWithCoords || $[9] !== searchQuery || $[10] !== selectedPartner || $[11] !== title) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const filteredPartners = searchQuery.trim() ? partners.filter(p_0 => {
        const q = searchQuery.toLowerCase();
        const name = `${p_0.firstName} ${p_0.lastName}`.toLowerCase();
        const location = (p_0.location || "").toLowerCase();
        const company = (p_0.company || "").toLowerCase();
        return name.includes(q) || location.includes(q) || company.includes(q);
      }) : partners;
      let t11;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = {
          lat: 39.8283,
          lng: -98.5795
        };
        $[19] = t11;
      } else {
        t11 = $[19];
      }
      const center = t11;
      let t12;
      if ($[20] !== partnersWithCoords) {
        t12 = map => {
          mapRef.current = map;
          if (partnersWithCoords.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            partnersWithCoords.forEach(partner => {
              if (partner.latitude && partner.longitude) {
                bounds.extend({
                  lat: partner.latitude,
                  lng: partner.longitude
                });
              }
            });
            map.fitBounds(bounds, {
              top: 30,
              right: 30,
              bottom: 30,
              left: 30
            });
          }
        };
        $[20] = partnersWithCoords;
        $[21] = t12;
      } else {
        t12 = $[21];
      }
      const onMapLoad = t12;
      const handlePartnerClick = partnerId => {
        setSelectedPartner(partnerId === selectedPartner ? null : partnerId);
        const partner_0 = partners.find(p_1 => p_1._id === partnerId);
        if (partner_0?.latitude && partner_0?.longitude && mapRef.current) {
          mapRef.current.panTo({
            lat: partner_0.latitude,
            lng: partner_0.longitude
          });
          mapRef.current.setZoom(10);
        }
      };
      if (partnersWithCoords.length === 0) {
        t10 = null;
        break bb0;
      }
      if (loadError) {
        let t13;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t13 = <section className="py-16 md:py-24 bg-white dark:bg-[#1a1a1a]"><div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16"><div className="text-center text-gray-500"><p>Unable to load map</p></div></div></section>;
          $[22] = t13;
        } else {
          t13 = $[22];
        }
        t10 = t13;
        break bb0;
      }
      t8 = "py-16 md:py-24 bg-white dark:bg-[#1a1a1a]";
      if ($[23] !== title) {
        t9 = <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 mb-12 md:mb-16"><h2 className="text-3xl md:text-4xl font-serif font-light text-[#1a1a1a] dark:text-white text-center tracking-wide">{title}</h2></div>;
        $[23] = title;
        $[24] = t9;
      } else {
        t9 = $[24];
      }
      t6 = "flex flex-col lg:flex-row gap-0 border-y border-[#e8e6e3] dark:border-gray-800 overflow-hidden";
      let t13;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#aaa]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
        $[25] = t13;
      } else {
        t13 = $[25];
      }
      let t14;
      if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = e => setSearchQuery(e.target.value);
        $[26] = t14;
      } else {
        t14 = $[26];
      }
      let t15;
      let t16;
      if ($[27] !== searchQuery) {
        t15 = <input type="text" value={searchQuery} onChange={t14} placeholder="Search by name, city, or state..." className="w-full pl-8 pr-8 py-1.5 text-sm border border-[#e8e6e3] dark:border-gray-700 bg-white dark:bg-[#252525] text-[#1a1a1a] dark:text-white placeholder-[#aaa] focus:outline-none focus:border-[var(--color-gold)] transition-colors" />;
        t16 = searchQuery && <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#aaa] hover:text-[#666] transition-colors"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>;
        $[27] = searchQuery;
        $[28] = t15;
        $[29] = t16;
      } else {
        t15 = $[28];
        t16 = $[29];
      }
      let t17;
      if ($[30] !== t15 || $[31] !== t16) {
        t17 = <div className="relative">{t13}{t15}{t16}</div>;
        $[30] = t15;
        $[31] = t16;
        $[32] = t17;
      } else {
        t17 = $[32];
      }
      t7 = <div className="w-full lg:w-[560px] lg:flex-shrink-0 max-h-[800px] overflow-y-auto bg-white dark:bg-[#1a1a1a]"><div className="sticky top-0 bg-white dark:bg-[#1a1a1a] border-b border-[#e8e6e3] dark:border-gray-800 p-3 z-10 space-y-2"><p className="text-sm text-[#6a6a6a] dark:text-gray-400 font-light">{filteredPartners.length} {filteredPartners.length === 1 ? "Partner" : "Partners"}{searchQuery ? " found" : " with locations"}</p>{t17}</div>{filteredPartners.map(partner_1 => <PartnerListCard key={partner_1._id} partner={partner_1} isSelected={selectedPartner === partner_1._id} onClick={() => handlePartnerClick(partner_1._id)} onMouseEnter={() => setHoveredPartner(partner_1._id)} onMouseLeave={() => setHoveredPartner(null)} />)}</div>;
      t4 = "w-full lg:flex-1 h-[600px] lg:h-[800px] bg-[#f0f0f0] dark:bg-gray-800";
      t5 = !isLoaded ? <div className="w-full h-full flex items-center justify-center"><div className="text-[#6a6a6a] dark:text-gray-400">Loading map...</div></div> : <GoogleMap mapContainerStyle={mapContainerStyle} center={center} zoom={7} options={mapOptions} onLoad={onMapLoad}>{partnersWithCoords.map(partner_2 => <PartnerMarker key={partner_2._id} partner={partner_2} isSelected={selectedPartner === partner_2._id} isHovered={hoveredPartner === partner_2._id} onClick={() => handlePartnerClick(partner_2._id)} />)}</GoogleMap>;
    }
    $[4] = hoveredPartner;
    $[5] = isLoaded;
    $[6] = loadError;
    $[7] = partners;
    $[8] = partnersWithCoords;
    $[9] = searchQuery;
    $[10] = selectedPartner;
    $[11] = title;
    $[12] = t10;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t10 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[33] !== t4 || $[34] !== t5) {
    t11 = <div className={t4}>{t5}</div>;
    $[33] = t4;
    $[34] = t5;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11 || $[37] !== t6 || $[38] !== t7) {
    t12 = <div className={t6}>{t7}{t11}</div>;
    $[36] = t11;
    $[37] = t6;
    $[38] = t7;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t12 || $[41] !== t8 || $[42] !== t9) {
    t13 = <section className={t8}>{t9}{t12}</section>;
    $[40] = t12;
    $[41] = t8;
    $[42] = t9;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
}
function _temp2(p) {
  return p.latitude !== null && p.latitude !== undefined && p.longitude !== null && p.longitude !== undefined;
}